import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils"

const buttonVariants = cva(
    "inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
    {
        variants: {
            variant: {
                default: "bg-primary text-primary-foreground",
                destructive:
                    "bg-destructive text-destructive-foreground hover-mouse:bg-destructive/90",
                outline:
                    "border border-input bg-background hover-mouse:text-accent-foreground",
                secondary:
                    "bg-secondary text-secondary-foreground hover-mouse:bg-secondary/80",
                ghost: "",
                link: "text-primary underline-offset-4 hover-mouse:underline",
                "icon-box": "bg-white control-responsive border-[1px] border-solid border-gray-200 w-10 h-10 hover-mouse:bg-gray-50 hover-mouse:border-gray-300 text-primary",
            },
            size: {
                default: "h-8 px-2 py-2",
                sm: "h-9 px-2",
                lg: "h-11 px-8",
                icon: "h-9 w-9",
            },
            radius: {
                default: "rounded-control",
                control: "rounded-control",
                card: "rounded-card",
                panel: "rounded-panel",
                dropdown: "rounded-dropdown",
                none: "rounded-none",
                // 반응형 variants
                "control-responsive": "rounded-control-mobile lg:rounded-control",
                "card-responsive": "rounded-card-mobile lg:rounded-card",
                "panel-responsive": "rounded-panel-mobile lg:rounded-panel",
            },
            iconSize: {
                xs: "[&_svg]:h-3 [&_svg]:w-3",      // 12px
                sm: "[&_svg]:h-4 [&_svg]:w-4",      // 16px
                md: "[&_svg]:h-5 [&_svg]:w-5",      // 20px
                lg: "[&_svg]:h-6 [&_svg]:w-6",      // 24px
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
            radius: "default",
            iconSize: "sm",  // 기본 16px
        },
        compoundVariants: [
            // icon-box variant default icon size (size omitted defaults to icon)
            {
                variant: "icon-box",
                size: "icon",
                className: "[&_svg]:h-5 [&_svg]:w-5",
            },
            // size variations
            {
                variant: "icon-box",
                size: "sm",
                className: "[&_svg]:h-3.5 [_svg]:w-3.5",
            },
            {
                variant: "icon-box",
                size: "lg",
                className: "[&_svg]:h-5 [_svg]:w-5",
            },
        ],
    }
)

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant = "default", size, radius, iconSize, asChild = false, ...props }, ref) => {
        // icon-box variant 에서는 size 를 지정하지 않으면 기본적으로 "icon" 으로 처리
        const finalSize = variant === "icon-box" && !size ? "icon" : size;
        const Comp = asChild ? Slot : "button";
        return (
            <Comp
                className={cn(buttonVariants({ variant, size: finalSize, radius, iconSize, className }))}
                ref={ref}
                {...props}
            />
        );
    }
);
Button.displayName = "Button"

export { Button, buttonVariants }
